/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/IncidenciasProfesorPage.tsx
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  Collapse,
  IconButton,
  Button,
  CircularProgress,
  Tooltip as MuiTooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Visibility from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useIncidenciasProfesorReport } from "../../../hooks/useReportJob";
import { getReservasIncidencia, getReserva, ReservaIncidencia } from "../../../api/reversas";
import ReservationFormDialog from "../../../components/ReservationFormDialog";

export default function IncidenciasProfesorPage() {
  const { t } = useTranslation<"translation">();
  /* ═════════ filtros de entrada ═════════ */
  const today = dayjs().format("YYYY-MM-DD");
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);
  const [search, setSearch] = useState("");

  /* ═════════ job resumen (incidencias por profesor) ═════════ */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useIncidenciasProfesorReport();
  const { data: status } = useStatus(jobId);

  /* ═════════ detalle por profesor (reservas incidentadas) ═════ */
  const [openRow, setOpenRow] = useState<number>(); // profesor abierto
  const [detalles, setDetalles] = useState<Record<number, ReservaIncidencia[]>>({});

  /* ═════════ diálogo reserva seleccionada ═════════ */
  const [selId, setSelId] = useState<number | null>(null);
  const [selData, setSelData] = useState<any | null>(null);
  const [loadingSel, setLoadingSel] = useState(false);

  /* ───── generar informe ───── */
  const launch = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
    setOpenRow(undefined);
    setDetalles({});
    setSelId(null);
    setSelData(null);
  };

  /* ───── expandir / colapsar detalle ───── */
  const toggleDetalle = async (profId: number) => {
    if (openRow === profId) {
      setOpenRow(undefined);
      return;
    }

    /* cache-in-memory: si no lo tenemos ya, lo pedimos */
    if (!detalles[profId]) {
      const data = await getReservasIncidencia(profId, desde, hasta);
      setDetalles((prev) => ({ ...prev, [profId]: data }));
    }
    setOpenRow(profId);
  };

  /* ───── abrir reserva concreta ───── */
  const abrirReserva = (id: number) => {
    setSelId(id);
    setSelData(null);
    setLoadingSel(true);
    getReserva(id)
      .then((r) => setSelData(r))
      .finally(() => setLoadingSel(false));
  };

  /* limpiar datos cuando cerramos el diálogo */
  useEffect(() => {
    if (!selId) setSelData(null);
  }, [selId]);

  /* ───── resumen filtrado por búsqueda ───── */
  const rows =
    status?.result?.rows.filter((r) => r.profesor.toLowerCase().includes(search.toLowerCase())) ??
    [];

  /* ═════════ helpers UI ═════════ */
  const renderComentarioCell = (raw?: string | null) => {
    const comentario = (raw ?? "").trim();
    const display = comentario || "—";
    return (
      <MuiTooltip
        title={
          <span style={{ whiteSpace: "pre-line" }}>
            {comentario || t<string>("incidenciasProfesor.detalle.sinComentarios", "—")}
          </span>
        }
        placement="top-start"
        arrow
      >
        <Typography
          variant="body2"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {display}
        </Typography>
      </MuiTooltip>
    );
  };

  /* ═════════ UI ═════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("incidenciasProfesor.titulo")}</Typography>

      {/* ─── barra de filtros ─── */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t("incidenciasProfesor.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("incidenciasProfesor.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <TextField
          size="small"
          placeholder={t<string>("incidenciasProfesor.filtros.buscar")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={launch}>
          {t("incidenciasProfesor.filtros.generar")}
        </Button>
      </Stack>

      {/* ─── progreso worker ─── */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("incidenciasProfesor.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* ─── resumen + detalle ─── */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t("incidenciasProfesor.estado.resultado", {
              count: status.result!.registros,
            })}
          </Alert>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <b>{t("incidenciasProfesor.tabla.profesor")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("incidenciasProfesor.tabla.incidencias")}</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => {
                  const det = detalles[r.profesorId] ?? [];
                  return (
                    <React.Fragment key={r.profesorId}>
                      {/* fila resumen */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleDetalle(r.profesorId)}>
                            {openRow === r.profesorId ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.profesor}</TableCell>
                        <TableCell align="right">{r.numInc}</TableCell>
                      </TableRow>

                      {/* filas detalle (reservas) */}
                      <TableRow>
                        {/* colSpan = 3 porque la tabla superior tiene 3 columnas */}
                        <TableCell style={{ padding: 0 }} colSpan={3}>
                          <Collapse in={openRow === r.profesorId} unmountOnExit>
                            <Paper variant="outlined" sx={{ m: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t("incidenciasProfesor.detalle.fecha")}</TableCell>
                                    <TableCell>
                                      {t("incidenciasProfesor.detalle.cliente")}
                                    </TableCell>
                                    <TableCell sx={{ width: "55%" }}>
                                      {t<string>(
                                        "incidenciasProfesor.detalle.comentarios",
                                        "Comentarios internos"
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {t("incidenciasProfesor.detalle.ver")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {det.map((d) => (
                                    <TableRow key={d.id}>
                                      <TableCell>{dayjs(d.fecha).format("YYYY-MM-DD")}</TableCell>
                                      <TableCell>{d.cliente}</TableCell>
                                      <TableCell sx={{ maxWidth: 520 }}>
                                        {renderComentarioCell(d.comentariosInternos)}
                                      </TableCell>
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => abrirReserva(d.id)}
                                          aria-label={t<string>(
                                            "incidenciasProfesor.detalle.abrirReserva",
                                            "Abrir reserva"
                                          )}
                                        >
                                          <Visibility fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* ─── diálogo de reserva ─── */}
      <ReservationFormDialog
        open={!!selId}
        mode="edit"
        readOnly
        reservaId={selId ?? undefined}
        initialValues={selData ?? undefined}
        onClose={() => setSelId(null)}
      >
        {loadingSel && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        )}
      </ReservationFormDialog>
    </Stack>
  );
}
